'use client';

import { useCallback, useEffect, useRef, useState } from 'react';
import { useTranslation } from 'next-i18next';
import NiceModal, { useModal } from '@ebay/nice-modal-react';
import { Modal, Spinner, bjToast } from '@components/common';
import AIInterviewFlow from '@components/templates/ai-interview/AiInterviewFlow';

export interface ResumeInterviewVideoModalProps {
  getQuestions: () => Promise<string[]>;
  locale: Locale;
  /** Invoked when the video flow completes (submit), before the modal closes. */
  onCompleted?: () => void;
}

interface VideoQuestion {
  type: string;
  question: string;
}

/**
 * Video-channel resume interview modal. Resolves the question strings, maps them to the shape
 * `AiInterviewFlow` expects (`{ type, question }`) and drives the existing video interview flow.
 */
export const ResumeInterviewVideoModal = NiceModal.create<ResumeInterviewVideoModalProps>(
  ({ getQuestions, locale, onCompleted }) => {
    const { t } = useTranslation('common');
    const modal = useModal();
    const [questions, setQuestions] = useState<VideoQuestion[] | null>(null);
    const startedRef = useRef(false);

    const closeModal = useCallback(() => {
      void modal.hide();
      modal.remove();
    }, [modal]);

    const handleComplete = useCallback(() => {
      onCompleted?.();
      closeModal();
    }, [onCompleted, closeModal]);

    useEffect(() => {
      if (startedRef.current) return;
      startedRef.current = true;

      void (async () => {
        try {
          const resolved = await getQuestions();
          if (!resolved.length) {
            bjToast.error(t('audio-interview.job-landing.error'));
            closeModal();
            return;
          }
          setQuestions(resolved.map((question) => ({ type: 'general', question })));
        } catch {
          bjToast.error(t('audio-interview.error.connecting'));
          closeModal();
        }
      })();
    }, [getQuestions, closeModal, t]);

    return (
      <Modal
        isOpen={modal.visible}
        onClose={closeModal}
        size="lg"
        className="!rounded-3xl"
        body={questions ? (
          // AiInterviewFlow is normally page-wrapped in a padded container (CvBasedAiInterview);
          // in a modal it renders bare, so add the padding here.
          <div className="p-4 sm:p-6">
            <AIInterviewFlow
              questionsFromApi={questions}
              autostart
              locale={locale}
              fileNamePrefix="iVideo"
              firebaseFolder="resume_interview_video"
              onComplete={handleComplete}
            />
          </div>
        ) : (
          <div className="flex min-h-64 items-center justify-center">
            <Spinner />
          </div>
        )}
      />
    );
  },
);
